import { Injectable, NgZone, inject } from '@angular/core';

import { Observable, of, switchMap } from 'rxjs';

import { EnvironmentService, JsLoader, WindowRefService } from '@hdms/shared/util-core';

interface RecaptchaWindow extends Window {
  grecaptcha: { execute(siteKey: string, params: { action: string }): Promise<string> };
}

interface GenerateTokenParams {
  siteKey?: string;
  action?: string;
}

const RECAPTCHA_API_URL = 'https://www.google.com/recaptcha/api.js';

const SITE_KEYS: string[] = [];

function getSiteKeyIndex(siteKey: string): number {
  if (!SITE_KEYS.includes(siteKey)) {
    SITE_KEYS.push(siteKey);
  }

  return SITE_KEYS.indexOf(siteKey);
}

@Injectable({ providedIn: 'root' })
export class RecaptchaService {
  private readonly siteKey = inject(EnvironmentService).recaptchaV3PublicKey;
  private readonly jsLoader = inject(JsLoader);
  private readonly zone = inject(NgZone);
  private readonly win = inject<WindowRefService<RecaptchaWindow>>(WindowRefService).nativeWindow;

  /**
   * Uses reCAPTCHA v3 API to execute the action that will emit a token
   * for verification purposes on the backend. This service also uses
   * the MO specific recaptcha public key, which is made available in processEnv.
   * Note: The action 'recaptcha_v3_token' is only used to help admins track where the
   * token generations are coming from in the Google recaptcha console.
   * Additionally, action names are limited alphanumeric characters, slashes, and underscores.
   * @param params
   */
  generateV3Token$(params?: GenerateTokenParams): Observable<string> {
    const { siteKey, action } = { siteKey: this.siteKey, action: 'recaptcha_v3_token', ...params };

    return siteKey ? this.loadScript$(siteKey).pipe(switchMap(() => this.requestToken$(siteKey, action))) : of(null);
  }

  private requestToken$(siteKey: string, action: string): Observable<string> {
    return new Observable<string>(subs => {
      this.zone.runOutsideAngular(async () => {
        try {
          const token = await this.win.grecaptcha.execute(siteKey, { action });
          this.zone.run(() => {
            subs.next(token);
            subs.complete();
          });
        } catch (e) {
          subs.error(e);
        }
      });
    });
  }

  private loadScript$(siteKey: string): Observable<void> {
    return new Observable<void>(subs => {
      const callbackName = `hdmsRecaptchaLoaded${getSiteKeyIndex(siteKey)}`;
      const scriptUrl = `${RECAPTCHA_API_URL}?render=${siteKey}&onload=${callbackName}`;

      const callback = (): void => {
        subs.next();
        subs.complete();
        delete this.win[callbackName];
      };

      if (this.jsLoader.getScriptTag(scriptUrl)) {
        callback();
      } else {
        this.win[callbackName] = callback;
        this.jsLoader.loadScript$(scriptUrl).subscribe({ error: subs.error });
      }
    });
  }
}
